import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import DocumentPreviewModal from '../../components/ui/DocumentPreviewModal';
import * as criminalService from '../../services/criminalCaseRequests';
import type {
  CriminalAdditionalDocumentView,
  CriminalDocumentsView,
  CriminalSubmittedDocumentView,
} from '../../services/criminalCaseRequests';
import { getCaseRequestDocumentsEnriched } from '../../services/caseRequestDocumentsEnrichment';
import type { CriminalDocumentRow } from '../../types/criminalCaseRequest';
import type { WizardFlowKind } from '../../utils/wizardCategoryRoute';
import {
  CASE_DOC_ACCEPT,
  CASE_DOC_DELETED_SUCCESS,
  validateCaseDocumentFile,
} from '../../utils/caseDocumentUpload';
import { normalizeDocumentFileFields } from '../../utils/fileDisplayName';
import { setFlashToast } from '../../utils/flashToast';
import {
  buildSubmittedDocumentDisplayRows,
  displayDocumentFileName,
  formatDocUploadedAt,
  formatFileSizeLabel,
  hasDownloadableDocumentFile,
  submittedDocUiStatus,
} from './viewDocumentHelpers';

type Props = {
  caseRequestId: string;
  /** Used to merge category-specific intake attachments (e.g. Civil Advise previous advice). */
  flow?: WizardFlowKind;
};

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function WarnIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function MinusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <line x1="8" y1="12" x2="16" y2="12" />
    </svg>
  );
}

function FileIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

function toPreviewDoc(
  id: string,
  fileName: string,
  originalFileName: string | null,
  documentName: string,
  uploadedAt: string,
): CriminalDocumentRow {
  const label = displayDocumentFileName({ originalFileName, fileName });
  return {
    id,
    checklistItemId: '',
    documentTypeId: '',
    documentName,
    mandatory: false,
    fileName,
    originalFileName: label === '—' ? '' : label,
    uploadTime: uploadedAt,
    skipReason: '',
  };
}

function SubmittedStatusCell({ doc }: { doc: CriminalSubmittedDocumentView }) {
  const uiStatus = submittedDocUiStatus(doc);
  if (uiStatus === 'uploaded') {
    return (
      <span className="ccr-review-status is-uploaded">
        <CheckIcon />
        Uploaded
      </span>
    );
  }
  if (uiStatus === 'pending') {
    return (
      <span className="ccr-review-status is-pending">
        <WarnIcon />
        Not Uploaded
      </span>
    );
  }
  return (
    <span className="ccr-review-status is-optional">
      <MinusIcon />
      Not Uploaded
    </span>
  );
}

export default function ViewRequestDocumentsTab({ caseRequestId, flow }: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [documents, setDocuments] = useState<CriminalDocumentsView>({ submitted: [], additional: [] });
  const [previewDoc, setPreviewDoc] = useState<CriminalDocumentRow | null>(null);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [uploadingAdditional, setUploadingAdditional] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<CriminalAdditionalDocumentView | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    const r = await getCaseRequestDocumentsEnriched(caseRequestId, flow);
    setLoading(false);
    if (!r.success) {
      const msg = r.message ?? t('viewRequest.documentsLoadFailed');
      setLoadError(msg);
      setFlashToast({ message: msg, variant: 'error' });
      return;
    }
    setDocuments(r.data);
  }, [caseRequestId, flow, t]);

  useEffect(() => {
    void loadDocuments();
  }, [loadDocuments]);

  const downloadFile = async (fileName: string, label: string) => {
    if (!fileName.trim()) return;
    const r = await criminalService.downloadCaseFile(fileName);
    if (!r.success) {
      setFlashToast({ message: r.message ?? 'Download failed.', variant: 'error' });
      return;
    }
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = label && label !== '—' ? label : 'document';
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadAll = async () => {
    if (downloadingAll) return;
    setDownloadingAll(true);
    const r = await criminalService.downloadAllCaseRequestDocuments(caseRequestId);
    setDownloadingAll(false);
    if (!r.success) {
      setFlashToast({ message: r.message ?? 'Download all failed.', variant: 'error' });
      return;
    }
    const url = URL.createObjectURL(r.data.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = r.data.fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onAdditionalFileSelected = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const validation = validateCaseDocumentFile(file);
    if (!validation.ok) {
      setFlashToast({ message: validation.message, variant: 'error' });
      return;
    }

    setUploadingAdditional(true);
    const uploadRes = await criminalService.uploadCaseFile(file);
    if (!uploadRes.success) {
      setUploadingAdditional(false);
      setFlashToast({ message: uploadRes.message ?? 'File upload failed.', variant: 'error' });
      return;
    }

    const { fileName: storageKey, originalFileName: displayName } = normalizeDocumentFileFields(
      uploadRes.data.file_name ?? '',
      uploadRes.data.original_file_name ?? file.name,
    );
    if (!storageKey) {
      setUploadingAdditional(false);
      setFlashToast({ message: 'Upload succeeded but file name was not returned.', variant: 'error' });
      return;
    }

    const registerRes = await criminalService.uploadAdditionalCaseDocument(
      caseRequestId,
      storageKey,
      displayName || file.name,
    );
    setUploadingAdditional(false);
    if (!registerRes.success) {
      setFlashToast({ message: registerRes.message ?? 'Could not register additional document.', variant: 'error' });
      return;
    }

    setFlashToast({ message: 'Additional document uploaded successfully.', variant: 'success' });
    void loadDocuments();
  };

  const requestDeleteAdditional = (doc: CriminalAdditionalDocumentView) => {
    if (deletingId) return;
    setPendingDelete(doc);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteAdditional = async () => {
    const doc = pendingDelete;
    if (!doc) {
      setDeleteConfirmOpen(false);
      return;
    }

    setDeleteConfirmOpen(false);
    setPendingDelete(null);
    setDeletingId(doc.id);

    const r = await criminalService.deleteAdditionalCaseDocument(caseRequestId, doc.id);
    setDeletingId(null);
    if (!r.success) {
      setFlashToast({ message: r.message ?? 'Could not delete document.', variant: 'error' });
      return;
    }
    setFlashToast({ message: CASE_DOC_DELETED_SUCCESS, variant: 'success' });
    void loadDocuments();
  };

  const deleteConfirmMessage = 'Are you sure you want to delete? This action cannot be undone.';

  const hasUploadedSubmitted = documents.submitted.some((d) => d.fileName.trim());
  const submittedRows = buildSubmittedDocumentDisplayRows(documents.submitted);

  if (loading) {
    return <p className="vcr-loading">{t('viewRequest.loadingDocuments')}</p>;
  }

  if (loadError) {
    return <p className="vcr-error">{loadError}</p>;
  }

  return (
    <div className="vcr-docs">
      <input
        ref={fileInputRef}
        type="file"
        accept={CASE_DOC_ACCEPT}
        className="ccr-hidden-file"
        aria-hidden
        tabIndex={-1}
        onChange={(e) => void onAdditionalFileSelected(e)}
      />

      <DocumentPreviewModal isOpen={Boolean(previewDoc)} doc={previewDoc} onClose={() => setPreviewDoc(null)} />

      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        title="Delete Document"
        message={deleteConfirmMessage}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setPendingDelete(null);
        }}
        onConfirm={() => void confirmDeleteAdditional()}
      />

      <div className="vcr-docs-hdr">
        <h3 className="vcr-docs-title">Submitted Documents</h3>
        <button
          type="button"
          className="vcr-btn-primary"
          disabled={downloadingAll || !hasUploadedSubmitted}
          onClick={() => void downloadAll()}
        >
          <DownloadIcon />
          {downloadingAll ? 'Downloading…' : 'Download All'}
        </button>
      </div>

      <div className="ccr-review-doc-wrap vcr-doc-table-wrap">
        <table className="ccr-review-doc-table vcr-doc-table">
          <thead>
            <tr>
              <th>Document Type</th>
              <th>Status</th>
              <th>File / Reason</th>
              <th>Uploaded at</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {submittedRows.length ? (
              submittedRows.map(({ doc, showDocumentType, rowKey }) => {
                const uiStatus = submittedDocUiStatus(doc);
                const displayName = displayDocumentFileName(doc);
                const sizeLabel = formatFileSizeLabel(doc.fileSize);
                const canDownload = hasDownloadableDocumentFile(doc);
                return (
                  <tr key={rowKey}>
                    <td className={`ccr-review-doc-type${showDocumentType ? '' : ' is-continuation'}`}>
                      {showDocumentType ? doc.documentTypeName : null}
                    </td>
                    <td>
                      <SubmittedStatusCell doc={doc} />
                    </td>
                    <td>
                      {uiStatus === 'uploaded' ? (
                        <div className="ccr-review-file-cell">
                          <span className="ccr-review-file-ico">
                            <FileIcon />
                          </span>
                          <button
                            type="button"
                            className="ccr-review-file-name"
                            onClick={() =>
                              setPreviewDoc(
                                toPreviewDoc(
                                  doc.id,
                                  doc.fileName,
                                  doc.originalFileName,
                                  doc.documentTypeName,
                                  doc.uploadedAt,
                                ),
                              )
                            }
                          >
                            {displayName}
                            {sizeLabel ? <span className="vcr-file-size"> ({sizeLabel})</span> : null}
                          </button>
                        </div>
                      ) : (
                        <span className={`ccr-review-reason${doc.reason ? ' is-reason' : ''}`}>
                          {doc.reason || '—'}
                        </span>
                      )}
                    </td>
                    <td className="vcr-uploaded-at">{formatDocUploadedAt(doc.uploadedAt)}</td>
                    <td>
                      {canDownload ? (
                        <div className="vcr-doc-acts">
                          <button
                            type="button"
                            className="ccr-review-file-act"
                            aria-label={`View ${displayName}`}
                            data-tip="Preview"
                            onClick={() =>
                              setPreviewDoc(
                                toPreviewDoc(
                                  doc.id,
                                  doc.fileName,
                                  doc.originalFileName,
                                  doc.documentTypeName,
                                  doc.uploadedAt,
                                ),
                              )
                            }
                          >
                            <EyeIcon />
                          </button>
                          <button
                            type="button"
                            className="ccr-review-file-act"
                            aria-label={`Download ${displayName}`}
                            data-tip="Download"
                            onClick={() => void downloadFile(doc.fileName, displayName)}
                          >
                            <DownloadIcon />
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={5} className="vcr-doc-empty">
                  {t('common.noDataFound')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="vcr-upload-section">
        <h3 className="vcr-docs-title">Upload Additional Documents</h3>
        <button
          type="button"
          className="vcr-btn-primary"
          disabled={uploadingAdditional}
          onClick={() => fileInputRef.current?.click()}
        >
          <UploadIcon />
          {uploadingAdditional ? 'Uploading…' : 'Upload Additional Documents'}
        </button>
      </div>

      <div className="vcr-additional-wrap">
        <table className="ccr-review-doc-table vcr-doc-table vcr-additional-table">
          <thead>
            <tr>
              <th>File Name</th>
              <th>Uploaded at</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {documents.additional.length ? (
              documents.additional.map((doc) => {
                const displayName = displayDocumentFileName(doc);
                const sizeLabel = formatFileSizeLabel(doc.fileSize);
                const busy = deletingId === doc.id;
                return (
                  <tr key={doc.id}>
                    <td>
                      <div className="ccr-review-file-cell">
                        <span className="ccr-review-file-ico">
                          <FileIcon />
                        </span>
                        <button
                          type="button"
                          className="ccr-review-file-name"
                          onClick={() =>
                            setPreviewDoc(
                              toPreviewDoc(
                                doc.id,
                                doc.fileName,
                                doc.originalFileName,
                                'Additional document',
                                doc.uploadedAt,
                              ),
                            )
                          }
                        >
                          {displayName}
                          {sizeLabel ? <span className="vcr-file-size"> ({sizeLabel})</span> : null}
                        </button>
                      </div>
                    </td>
                    <td className="vcr-uploaded-at">{formatDocUploadedAt(doc.uploadedAt)}</td>
                    <td>
                      <div className="vcr-doc-acts">
                        <button
                          type="button"
                          className="ccr-review-file-act"
                          aria-label={`View ${displayName}`}
                          data-tip="Preview"
                          onClick={() =>
                            setPreviewDoc(
                              toPreviewDoc(
                                doc.id,
                                doc.fileName,
                                doc.originalFileName,
                                'Additional document',
                                doc.uploadedAt,
                              ),
                            )
                          }
                        >
                          <EyeIcon />
                        </button>
                        <button
                          type="button"
                          className="ccr-review-file-act"
                          aria-label={`Download ${displayName}`}
                          data-tip="Download"
                          onClick={() => void downloadFile(doc.fileName, displayName)}
                        >
                          <DownloadIcon />
                        </button>
                        <button
                          type="button"
                          className="ccr-review-file-act is-danger"
                          aria-label={`Delete ${displayName}`}
                          data-tip="Delete"
                          disabled={busy}
                          onClick={() => requestDeleteAdditional(doc)}
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={3} className="vcr-doc-empty">
                  {t('common.noDataFound')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
